import React, { useState } from 'react';
import { Button, Card, Input, Space } from 'antd';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { DeleteOutlined, DragOutlined, PlusOutlined } from '@ant-design/icons';

import showDeleteConfirm from '../../../../components/Confirmation-modal';

const { TextArea } = Input;

export default function CustomSubsections({ customSubsections, onChange }) {
    const [newSectionTitle, setNewSectionTitle] = useState('');

    const handleAddSection = () => {
        if (!newSectionTitle.trim()) return;

        const newSection = {
            _id: `temp_${Date.now()}`,
            title: newSectionTitle,
            content: '',
            orderIndex: customSubsections.length
        };

        onChange([...customSubsections, newSection]);
        setNewSectionTitle('');
    };

    const handleUpdateSection = (index, field, value) => {
        const updated = [...customSubsections];
        updated[index] = { ...updated[index], [field]: value };
        onChange(updated);
    };

    const handleDeleteSection = (index, title) => {
        showDeleteConfirm(
            (confirmed) => {
                if (confirmed) {
                    const updated = customSubsections.filter((_, i) => i !== index);
                    onChange(updated);
                }
            },
            title,
            'Delete this subsection?'
        );
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(customSubsections);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update orderIndex
        const reordered = items.map((item, index) => ({
            ...item,
            orderIndex: index
        }));

        onChange(reordered);
    };

    return (
        <div className="custom-subsections">
            <h3>Custom Subsections</h3>

            {/* Add new section */}
            <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Input
                    placeholder="New subsection title"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    onPressEnter={handleAddSection}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSection}>
                    Add
                </Button>
            </Space.Compact>

            {/* Existing sections */}
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="custom-sections">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {customSubsections.map((section, index) => (
                                <Draggable
                                    key={section._id}
                                    draggableId={section._id}
                                    index={index}
                                >
                                    {(dragProvided) => (
                                        <Card
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            size="small"
                                            title={
                                                <Space>
                                                    <div {...dragProvided.dragHandleProps}>
                                                        <DragOutlined />
                                                    </div>
                                                    <Input
                                                        value={section.title}
                                                        onChange={(e) =>
                                                            handleUpdateSection(
                                                                index,
                                                                'title',
                                                                e.target.value
                                                            )
                                                        }
                                                        variant="borderless"
                                                        style={{ fontWeight: 'bold' }}
                                                    />
                                                </Space>
                                            }
                                            extra={
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    size="small"
                                                    onClick={() =>
                                                        handleDeleteSection(index, section.title)
                                                    }
                                                />
                                            }
                                            style={{ marginBottom: 16 }}
                                        >
                                            <TextArea
                                                value={section.content}
                                                onChange={(e) =>
                                                    handleUpdateSection(
                                                        index,
                                                        'content',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Enter content..."
                                                autoSize={{ minRows: 3, maxRows: 10 }}
                                            />
                                        </Card>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
}

CustomSubsections.propTypes = {
    customSubsections: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired
};
